import { useState } from "react";
import dayjs from "dayjs";
import DataTable from "../../../reusable/DataTable/DataTable";
import StatusChip from "../../../reusable/StatusChip/StatusChip";
import Button from "../../../reusable/Button/Button";
import StatusFilterChips from "../StatusFilterChips";
import FilterDropdown from "../../../reusable/FilterDropdown/FilterDropdown";
import Select from "../../../reusable/Select/Select";
import Input from "../../../reusable/Input/Input";
import TextArea from "../../../reusable/TextArea/TextArea";
import useAffiliateRequests, { formatBalance, requestStatusTone } from "../useAffiliateRequests";
import { CAMPAIGNS } from "./data";
import { t } from "../../../i18n/i18n";
import "./Giveaway.scss";

const icon = (name) => ({ "--icon": `url(/icons/${name}.svg)` });

/** `/affiliate/requests/options` limits, for when they haven't arrived yet. */
const DEFAULT_MAX_QUANTITY = 50;
const DEFAULT_MIN_DAYS = 4;
const DEFAULT_PURPOSE_MAX = 120;

/** The dropdown offers what the chips do — they share one filter value. */
const STATUS_OPTIONS = [
  { label: "All", value: null },
  { label: "Approved", value: "Approved" },
  { label: "Processing", value: "Processing" },
  { label: "Rejected", value: "Rejected" },
];

const DATE_FORMATTER = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric" });
const formatDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? String(value) : DATE_FORMATTER.format(d);
};

const columns = [
  {
    key: "accountSize",
    header: "Account Size",
    width: 16,
    value: (r) => formatBalance(r.account_size ?? r.challenge?.account_balance),
  },
  { key: "quantity", header: "Quantity", width: 10, value: (r) => r.quantity ?? "—" },
  // Null until an admin approves it — then how many of the asked-for accounts were granted.
  { key: "allotted", header: "Allotted", width: 10, value: (r) => r.accounts_allotted ?? "—" },
  { key: "winners", header: "Winners", width: 10, value: (r) => r.winners?.submitted ?? 0 },
  { key: "endDate", header: "Ends", width: 12, value: (r) => formatDate(r.end_date) },
  { key: "purpose", header: "Purpose", width: 26, value: (r) => r.purpose || "—" },
  {
    key: "status",
    header: "Status",
    width: 14,
    render: (r) => (
      <StatusChip tone={requestStatusTone(r.status)}>{t(r.status || "Processing")}</StatusChip>
    ),
  },
];

/**
 * Affiliate — Giveaway. New request form + request history — 1:17457.
 * `/affiliate/requests/?request_type=giveaway`, through `useAffiliateRequests`.
 */
export default function Giveaway() {
  const {
    limits,
    challengeOptions,
    platformOptions,
    isOptionsLoading,
    eligibility,
    statusCounts,
    statusFilter,
    setStatusFilter,
    table,
    submit,
    isSubmitting,
  } = useAffiliateRequests("giveaway");

  const maxQuantity = limits.max_giveaway_quantity ?? DEFAULT_MAX_QUANTITY;
  const minDays = limits.min_giveaway_duration_days ?? DEFAULT_MIN_DAYS;
  const purposeMax = limits.purpose_max_length ?? DEFAULT_PURPOSE_MAX;
  const earliestEnd = dayjs().add(minDays, "day").startOf("day");

  const [challenge, setChallenge] = useState("");
  const [platform, setPlatform] = useState("");
  const [quantity, setQuantity] = useState("");
  const [endDate, setEndDate] = useState("");
  const [purpose, setPurpose] = useState("");

  // The first platform until one is picked — every request needs one.
  const chosenPlatform = platform || platformOptions[0]?.value || "";
  const notEligible = eligibility?.eligible === false;
  const qty = Number(quantity);

  const canSubmit = Boolean(
    !notEligible &&
      challenge &&
      chosenPlatform &&
      qty >= 1 &&
      qty <= maxQuantity &&
      endDate &&
      !dayjs(endDate).isBefore(earliestEnd, "day") &&
      purpose.trim()
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit || isSubmitting) return;

    const ok = await submit(
      {
        challenge: Number(challenge),
        platform: chosenPlatform,
        quantity: qty,
        purpose: purpose.trim(),
        end_date: endDate,
      },
      t("Giveaway request submitted")
    );
    if (ok) {
      setChallenge("");
      setQuantity("");
      setEndDate("");
      setPurpose("");
    }
  };

  return (
    <div className="affiliate-giveaway">
      <div className="affiliate-giveaway__toolbar">
        <StatusFilterChips counts={statusCounts} value={statusFilter} onChange={setStatusFilter} />
      </div>

      <div className="affiliate-giveaway__body">
        <form className="giveaway-form" onSubmit={handleSubmit}>
          <div className="giveaway-form__head">
            <span className="icon giveaway-form__icon" style={icon("affiliate-plus")} />
            <span className="giveaway-form__title">{t("New Giveaway Request")}</span>
          </div>
          <p className="giveaway-form__subtitle">{t("Submit for admin approval")}</p>

          {notEligible && (
            <p className="giveaway-form__footnote" role="alert">
              {eligibility.reason || t("Your account can't request giveaways yet.")}
            </p>
          )}

          <Select
            label="Challenge"
            placeholder={isOptionsLoading ? t("Loading challenges...") : t("Select challenge")}
            value={challenge}
            onChange={(e) => setChallenge(e.target.value)}
            options={challengeOptions}
            required
            variant="form"
            disabled={notEligible}
          />

          <Select
            label="Platform"
            placeholder="Select platform"
            value={chosenPlatform}
            onChange={(e) => setPlatform(e.target.value)}
            options={platformOptions}
            required
            variant="form"
            disabled={notEligible}
          />

          <Input
            label="Quantity"
            type="number"
            min={1}
            max={maxQuantity}
            placeholder="Enter quantity"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            hint={t("1 to {{max}} accounts", { max: maxQuantity })}
            error={quantity && qty > maxQuantity ? t("At most {{max}} accounts per giveaway", { max: maxQuantity }) : undefined}
            required
            variant="form"
            disabled={notEligible}
          />

          <Input
            label="End Date"
            type="date"
            placeholder="Select date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            // The giveaway has to run at least `minDays` — earlier days can't be picked.
            disabledDate={(d) => d && d.isBefore(earliestEnd, "day")}
            defaultPickerValue={earliestEnd}
            hint={t("At least {{days}} days from today", { days: minDays })}
            required
            variant="form"
            disabled={notEligible}
          />

          <TextArea
            label="Purpose"
            placeholder="Describe the purpose of this giveaway ..."
            value={purpose}
            onChange={(e) => setPurpose(e.target.value)}
            maxLength={purposeMax}
            required
            variant="form"
            disabled={notEligible}
          />

          <div className="giveaway-campaigns">
            {CAMPAIGNS.map((c) => (
              <button
                key={c}
                type="button"
                className={`giveaway-campaign${purpose === c ? " is-active" : ""}`}
                onClick={() => setPurpose(c)}
                disabled={notEligible}
              >
                {t(c)}
              </button>
            ))}
          </div>

          <Button
            variant="pill"
            className="giveaway-submit"
            height={28.8}
            paddingInline={16}
            htmlType="submit"
            disabled={!canSubmit || isSubmitting}
            loading={isSubmitting}
          >
            {t("Submit Request")}
          </Button>

          <p className="giveaway-form__footnote">
            {t("Requests remain editable until reviewed by an administrator.")}
          </p>
        </form>

        <DataTable
          className="affiliate-giveaway__table"
          icon="affiliate-recent-requests"
          title="Request History"
          count={`${table.total}`}
          columns={columns}
          rows={table.rows}
          getRowId={(r) => r.id}
          total={table.total}
          page={table.page}
          pageSize={table.pageSize}
          onPageChange={table.onPageChange}
          onSearch={table.onSearch}
          loading={table.loading}
          emptyMessage={table.emptyMessage}
          searchPlaceholder="Search requests"
          minWidth={620}
          actions={
            <FilterDropdown
              options={STATUS_OPTIONS}
              value={statusFilter}
              onChange={setStatusFilter}
              label="Filters"
            />
          }
        />
      </div>
    </div>
  );
}
